import {
  capabilitiesFor,
  directionForText,
  splitParagraphs,
  suggestionChips,
  type ScheduleCard as ScheduleCardData,
} from '@pa/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { directionFor } from '../i18n';
import { dayKey, deviceZone, formatDate, timeFormatter, zoneLabel } from '../lib/format';
import type { ChatMessage, LiveState } from '../lib/agent';
import { greetingKey } from '../lib/greeting';
import { useSession } from '../lib/session';
import type {
  AgentTurnState,
  ApprovalFrame,
  ApprovalResultFrame,
  ConnectionActionFrame,
  ScheduleFrame,
  StepFrame,
} from '../lib/ws';
import { ReconnectAction } from './ui/ReconnectAction';
import { ApprovalCard, type ApprovalCardState } from './ui/ApprovalCard';
import { ScheduleCard } from './ui/ScheduleCard';
import { AttachmentList } from './Attachments';
import { BrandMark } from './ui/BrandMark';
import { AlertCircle, Clock, Check, Close, Spinner } from './ui/icons';
import { LiveMark } from './ui/LiveMark';

/** Distance from the bottom (px) within which the list counts as "at the bottom". */
const NEAR_BOTTOM_PX = 40;

/**
 * The conversation. This is the only scroller of the TWO screen: it keeps its position across
 * viewport changes (keyboard open / close), stays pinned to the bottom when it was within 40px
 * of it, and never calls scrollIntoView (Safari would scroll the document).
 */
export function MessageList({
  messages,
  draft,
  turnState,
  turnLabel = null,
  steps,
  loading,
  hasMore,
  onLoadMore,
  firstName,
  onSuggest,
  focusEventId,
  onFocused,
  approvals = [],
  schedules = [],
  connectionActions = [],
  approvalResults = {},
  onApprove,
  onCancel,
  onRetry,
}: {
  /** M5.1: send a message that was not delivered again. */
  onRetry?: ((clientId: string) => void) | undefined;
  messages: readonly ChatMessage[];
  draft: { trace_id: string; text: string } | null;
  turnState: AgentTurnState | null;
  /** Live action label under the state ("Transcribing…"), from state and step frames. */
  turnLabel?: string | null;
  steps: readonly StepFrame[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void | Promise<void>;
  firstName: string;
  onSuggest: (text: string) => void;
  /** Event id to scroll to and highlight (from the memory screen's source link). */
  focusEventId?: string | null | undefined;
  onFocused?: (() => void) | undefined;
  /** Approval cards of this conversation and their outcomes (from the agent store). */
  approvals?: readonly ApprovalFrame[] | undefined;
  /** Live schedule cards (events, free slots) not yet carried by a persisted bubble. */
  schedules?: readonly ScheduleFrame[] | undefined;
  /** Inline "Reconnect Google" affordances (one per trace). */
  connectionActions?: readonly ConnectionActionFrame[] | undefined;
  approvalResults?: Readonly<Record<string, ApprovalResultFrame>> | undefined;
  onApprove?: ((approval: ApprovalFrame) => Promise<void>) | undefined;
  onCancel?: ((approval: ApprovalFrame) => Promise<void>) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const [now] = useState(() => Date.now());
  const uiDir = directionFor(i18n.resolvedLanguage ?? 'en');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLOListElement>(null);
  const atBottomRef = useRef(true);

  const empty = messages.length === 0 && !draft && !turnState;
  const listMounted = !loading && !empty;
  const lastMessage = messages.at(-1);

  // Track whether the person is at the bottom; keep the bottom pinned across size changes of
  // the scroller (keyboard, orientation, the root's height transition) and of its content.
  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const measure = () => {
      atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };
    const keep = () => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(keep);
    observer.observe(el);
    observer.observe(content);
    keep();
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [listMounted]);

  // Source-message link: scroll the list (never the document) to the message and highlight it
  // for two seconds with the soft-blue background (brand easing, no bounce).
  useEffect(() => {
    if (!focusEventId) return;
    const scroller = scrollerRef.current;
    const el = scroller?.querySelector<HTMLElement>(
      `[data-event-id="${CSS.escape(focusEventId)}"]`,
    );
    if (!scroller || !el) return;
    const top =
      el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTop = Math.max(0, top - 24);
    atBottomRef.current = false;
    el.classList.add('msg-highlight');
    const t = setTimeout(() => el.classList.remove('msg-highlight'), 2000);
    onFocused?.();
    return () => clearTimeout(t);
  }, [focusEventId, messages.length, onFocused]);

  // New content: follow it when at the bottom, or when the person just sent a message.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (atBottomRef.current || lastMessage?.role === 'user') {
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
    }
  }, [
    messages.length,
    draft?.text.length,
    turnState,
    steps.length,
    lastMessage?.role,
    approvals.length,
  ]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6" role="status">
        <LiveMark state="thinking" size={20} title={t('common.loading')} />
      </div>
    );
  }

  if (empty) {
    const chips = suggestionChips(capabilitiesFor({ connected: (me?.connections ?? 0) > 0 }));
    return (
      <div className="scroller flex flex-1 flex-col items-center justify-center gap-6 p-6">
        <div className="text-center">
          <p className="text-xl font-medium tracking-tight text-ink text-balance">
            {t(`two.greeting.${greetingKey()}`, { name: firstName })}
          </p>
          <p className="mt-2 text-base text-ink-2 text-balance">{t('two.prompt')}</p>
        </div>
        <ul className="flex flex-wrap justify-center gap-2">
          {chips.map((k) => (
            <li key={k}>
              <button
                type="button"
                onClick={() => onSuggest(t(`two.chips.${k}`))}
                className="min-h-11 rounded-full border border-line bg-surface px-4 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-brand-soft"
              >
                {t(`two.chips.${k}`)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // M5.3: every time in the device's zone; the zone is named on the day separator and once
  // per day group when the device zone differs from the profile zone.
  const locale = i18n.resolvedLanguage ?? 'en';
  const timeZone = deviceZone();
  const profileZone = me?.timezone ?? timeZone;
  const timeFormat = timeFormatter(locale, { timeZone });
  const zoneDiffers = profileZone !== timeZone;
  const zoneName = zoneLabel(timeZone, locale);
  const live: LiveState | null = turnState;
  // Streaming: the draft is split at blank lines with the same rule as the server's chunkReply,
  // so the final bubbles land exactly where the cards already are (no reflow).
  const draftCards = draft?.text ? splitParagraphs(draft.text) : [];
  // Each approval card sits after TWO's last bubble of its trace; without one, at the end.
  const cardsAfter = new Map<string, ApprovalFrame[]>();
  const trailing: ApprovalFrame[] = [];
  for (const a of approvals) {
    let anchor: string | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'agent' && m.trace_id === a.trace_id) {
        anchor = m.id;
        break;
      }
    }
    if (anchor) cardsAfter.set(anchor, [...(cardsAfter.get(anchor) ?? []), a]);
    else trailing.push(a);
  }
  // Schedule cards: those already on a bubble render with it; live frames not yet carried by a
  // bubble sit after TWO's last bubble of their trace, or at the end.
  const carried = new Set(messages.flatMap((m) => (m.cards ?? []).map((c) => c.card_id)));
  const schedulesAfter = new Map<string, ScheduleFrame[]>();
  const trailingSchedules: ScheduleFrame[] = [];
  for (const s of schedules) {
    if (carried.has(s.card_id)) continue;
    let anchor: string | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'agent' && m.trace_id === s.trace_id) {
        anchor = m.id;
        break;
      }
    }
    if (anchor) schedulesAfter.set(anchor, [...(schedulesAfter.get(anchor) ?? []), s]);
    else trailingSchedules.push(s);
  }
  const actionsAfter = new Map<string, ConnectionActionFrame[]>();
  const trailingActions: ConnectionActionFrame[] = [];
  for (const a of connectionActions) {
    let anchor: string | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'agent' && m.trace_id === a.trace_id) {
        anchor = m.id;
        break;
      }
    }
    if (anchor) actionsAfter.set(anchor, [...(actionsAfter.get(anchor) ?? []), a]);
    else trailingActions.push(a);
  }
  const renderAction = (a: ConnectionActionFrame) => (
    <li key={`${a.trace_id}:${a.provider}`} className="max-w-[92%]">
      <ReconnectAction provider={a.provider} action={a.action} reason={a.reason} />
    </li>
  );
  const renderSchedule = (c: ScheduleCardData) => (
    <li key={c.card_id} className="max-w-[92%]">
      <ScheduleCard card={c} onUse={onSuggest} />
    </li>
  );
  const renderCard = (a: ApprovalFrame) => {
    const result = approvalResults[a.approval_id];
    const state: ApprovalCardState = result
      ? result.status === 'approved'
        ? 'approved'
        : result.status
      : new Date(a.expires_at).getTime() < now
        ? 'expired'
        : 'pending';
    return (
      <li key={a.approval_id} className="max-w-[92%]">
        <ApprovalCard
          approval={a}
          state={state}
          checklist={result?.checklist ?? []}
          onApprove={() => onApprove?.(a) ?? Promise.resolve()}
          onCancel={() => onCancel?.(a) ?? Promise.resolve()}
        />
      </li>
    );
  };

  return (
    <div ref={scrollerRef} className="scroller min-h-0 flex-1">
      <ol ref={contentRef} className="flex flex-col gap-4 px-4 py-4" aria-live="polite">
        {hasMore ? (
          <li className="text-center">
            <button
              type="button"
              onClick={() => void onLoadMore()}
              className="min-h-11 rounded-md px-3 text-sm font-medium text-brand hover:bg-brand-soft"
            >
              {t('two.loadOlder')}
            </button>
          </li>
        ) : null}
        {messages.flatMap((m, i) => [
          ...(i === 0 || dayKey(messages[i - 1]?.at ?? m.at, timeZone) !== dayKey(m.at, timeZone)
            ? [
                <li
                  key={`day-${dayKey(m.at, timeZone)}`}
                  className="flex items-center gap-3 py-1 text-xs text-ink-3"
                  role="separator"
                  aria-label={formatDate(new Date(m.at), locale, timeZone)}
                >
                  <span aria-hidden className="h-px flex-1 bg-line" />
                  <span className="tabular whitespace-nowrap">
                    {formatDate(new Date(m.at), locale, timeZone)}
                    {zoneDiffers ? ` · ${zoneLabel(timeZone, locale)}` : ''}
                  </span>
                  <span aria-hidden className="h-px flex-1 bg-line" />
                </li>,
              ]
            : []),
          m.role === 'user' ? (
            <UserMessage
              key={m.id}
              message={m}
              uiDir={uiDir}
              timeFormat={timeFormat}
              zone={
                zoneDiffers &&
                (i === 0 ||
                  dayKey(messages[i - 1]?.at ?? m.at, timeZone) !== dayKey(m.at, timeZone))
                  ? zoneName
                  : undefined
              }
              t={t}
              onRetry={onRetry}
            />
          ) : (
            <AgentMessage
              key={m.id}
              message={m}
              uiDir={uiDir}
              timeFormat={timeFormat}
              zone={
                zoneDiffers &&
                (i === 0 ||
                  dayKey(messages[i - 1]?.at ?? m.at, timeZone) !== dayKey(m.at, timeZone))
                  ? zoneName
                  : undefined
              }
              t={t}
            />
          ),
          ...(m.cards ?? []).map(renderSchedule),
          ...(schedulesAfter.get(m.id) ?? []).map(renderSchedule),
          ...(cardsAfter.get(m.id) ?? []).map(renderCard),
          ...(actionsAfter.get(m.id) ?? []).map(renderAction),
        ])}
        {trailingSchedules.map(renderSchedule)}
        {trailingActions.map(renderAction)}
        {trailing.map(renderCard)}
        {draft
          ? draftCards.map((text, i) => (
              <AgentMessage
                key={`draft-${draft.trace_id}-${i}`}
                message={{
                  id: `draft-${draft.trace_id}-${i}`,
                  role: 'agent',
                  text,
                  at: new Date().toISOString(),
                  reactions: [],
                  attachments: [],
                  pending: true,
                }}
                uiDir={uiDir}
                timeFormat={timeFormat}
                t={t}
                streaming
                cursor={i === draftCards.length - 1}
              />
            ))
          : null}
        {live ? (
          <li className="flex flex-col gap-2 px-1" role="status">
            <div className="flex items-center gap-3 text-sm text-ink-2">
              <LiveMark state={live} size={16} />
              <span
                className={
                  live === 'done'
                    ? 'text-success-text'
                    : live === 'waiting'
                      ? 'text-attention-text'
                      : ''
                }
              >
                {live === 'working' && turnLabel ? turnLabel : t(`two.live.${live}`)}
              </span>
            </div>
            {steps.length > 0 ? <WorkingPanel steps={steps} t={t} /> : null}
          </li>
        ) : null}
      </ol>
    </div>
  );
}

type Tx = ReturnType<typeof useTranslation>['t'];

/** "TWO is working" panel: rendered only when real step frames exist for the turn. */
function WorkingPanel({ steps, t }: { steps: readonly StepFrame[]; t: Tx }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-sm dark:bg-elevated">
      <p className="mb-2 text-xs font-medium text-ink-2">{t('two.working')}</p>
      <ol className="flex flex-col gap-1.5">
        {steps.map((s) => (
          <li key={s.step_id} className="flex items-center gap-2 text-sm text-ink">
            {s.status === 'running' ? (
              <Spinner size={14} className="text-brand" />
            ) : s.status === 'done' ? (
              <Check size={14} className="text-success" strokeWidth={2.5} />
            ) : (
              <Close size={14} className="text-critical" />
            )}
            <span dir="auto">{s.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Plain ink text on the canvas, end-aligned. No bubble. Attachments as small surface controls. */
function UserMessage({
  message: m,
  uiDir,
  timeFormat,
  zone,
  t,
  onRetry,
}: {
  message: ChatMessage;
  uiDir: 'rtl' | 'ltr';
  timeFormat: Intl.DateTimeFormat;
  /** M5.3: the zone name, on the first message of a day group when zones differ. */
  zone?: string | undefined;
  t: Tx;
  onRetry?: ((clientId: string) => void) | undefined;
}) {
  const dir = directionForText(m.text, uiDir);
  const isVoice = m.attachments.some((a) => a.kind === 'audio');
  return (
    <li
      data-event-id={m.id}
      className={[
        'msg -mx-2 flex flex-col items-end gap-1 rounded-md px-2',
        m.pending ? 'opacity-60' : '',
      ].join(' ')}
    >
      <div className="sr-only">{t('two.you')}</div>
      <AttachmentList
        attachments={m.attachments.filter((a) => a.kind !== 'link')}
        localUrls={m.localUrls}
        align="end"
      />
      {m.text && !isVoice ? (
        <p
          data-bubble="you"
          className={[
            'max-w-[85%] whitespace-pre-wrap rounded-lg bg-brand-soft px-4 py-3 text-base text-ink [overflow-wrap:anywhere]',
            dir === 'rtl' ? 'text-right' : 'text-left',
          ].join(' ')}
          dir={dir}
          lang={dir === 'rtl' ? 'ar' : 'en'}
        >
          {m.text}
        </p>
      ) : null}
      <AttachmentList attachments={m.attachments.filter((a) => a.kind === 'link')} align="end" />
      <div className="flex items-center gap-2 text-xs text-ink-3">
        {m.reactions.length ? (
          <Reactions reactions={m.reactions} label={t('two.reactions')} />
        ) : null}
        {m.delivery === 'sending' ? (
          <span className="inline-flex items-center gap-1" role="status" aria-live="polite">
            <Clock size={12} aria-hidden />
            <span className="sr-only">{t('two.sending')}</span>
          </span>
        ) : null}
        <time dateTime={m.at} className="tabular">
          <bdi dir="ltr">
            {timeFormat.format(new Date(m.at))}
            {zone ? ` · ${zone}` : ''}
          </bdi>
        </time>
      </div>
      {m.delivery === 'failed' ? (
        <p
          role="status"
          className="flex max-w-[85%] items-center gap-1.5 text-sm text-critical-text"
          dir="auto"
        >
          <AlertCircle size={16} className="shrink-0" />
          <span>{t('two.notDelivered')}</span>
          {onRetry ? (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                onClick={() => onRetry(m.id)}
                className="min-h-8 rounded-md px-1 font-medium text-brand underline-offset-2 hover:underline"
              >
                {t('two.retry')}
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {m.error ? (
        <p
          role="status"
          className="flex max-w-[85%] items-start gap-1.5 text-sm text-critical-text"
          dir="auto"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{m.error}</span>
        </p>
      ) : m.note ? (
        <p role="status" className="max-w-[85%] text-sm text-ink-2" dir="auto">
          {m.note}
        </p>
      ) : null}
    </li>
  );
}

/** TWO message: a surface card (dark: elevated) with the 16px brand mark; no avatar. */
function AgentMessage({
  message: m,
  uiDir,
  timeFormat,
  zone,
  t,
  streaming = false,
  cursor = false,
}: {
  message: ChatMessage;
  uiDir: 'rtl' | 'ltr';
  timeFormat: Intl.DateTimeFormat;
  /** M5.3: the zone name, on the first message of a day group when zones differ. */
  zone?: string | undefined;
  t: Tx;
  streaming?: boolean;
  /** Show the streaming caret (last card of a streamed draft only). */
  cursor?: boolean;
}) {
  const dir = directionForText(m.text, uiDir);
  return (
    <li data-event-id={m.id} className="msg -mx-2 flex justify-start rounded-md px-2">
      <div
        data-bubble="two"
        dir={dir}
        className="max-w-[92%] rounded-lg border border-line bg-surface px-4 py-3 shadow-sm dark:bg-elevated"
      >
        <div className="mb-1.5 flex items-center gap-2" dir={dir}>
          <BrandMark size={16} />
          <span className="sr-only">{t('two.agent')}</span>
        </div>
        <p
          className={[
            'whitespace-pre-wrap text-base text-ink [overflow-wrap:anywhere]',
            dir === 'rtl' ? 'text-right' : 'text-left',
          ].join(' ')}
          dir={dir}
          lang={dir === 'rtl' ? 'ar' : 'en'}
        >
          {m.text}
          {streaming && cursor ? <span className="animate-pulse text-brand">▍</span> : null}
        </p>
        {!streaming ? (
          <time dateTime={m.at} className="tabular mt-1.5 block text-end text-xs text-ink-3">
            <bdi dir="ltr">
              {timeFormat.format(new Date(m.at))}
              {zone ? ` · ${zone}` : ''}
            </bdi>
          </time>
        ) : null}
      </div>
    </li>
  );
}

/** Brand reaction set: 👀 and 👍 as text, ✓ as a success-coloured check. */
function Reactions({ reactions, label }: { reactions: readonly string[]; label: string }) {
  return (
    <span className="inline-flex items-center gap-1" aria-label={label}>
      {reactions.map((r) =>
        r === '✓' || r === '✅' ? (
          <Check key={r} size={14} className="text-success" strokeWidth={2.5} />
        ) : (
          <span key={r} className="text-sm leading-none">
            {r}
          </span>
        ),
      )}
    </span>
  );
}
